(() => {
  if (customElements.get('product-page')) return;

  const fill = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const UNITS_KEY = 'jglam:size-units';

  const applySizeUnits = (unit) => {
    const value = unit === 'cm' ? 'cm' : 'in';
    document.documentElement.dataset.sizeUnits = value;
    document.querySelectorAll('[data-size-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sizeUnit === value));
    });
    return value;
  };

  const storedSizeUnits = () => {
    try {
      return window.localStorage.getItem(UNITS_KEY);
    } catch (error) {
      return null;
    }
  };

  class ProductPage extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
      this.form = this.querySelector('form.pdp__form');
      this.idInput = this.querySelector('[data-variant-input]');
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.optionCount = this.variants[0]?.options.length || 0;
      this.atc = this.querySelector('[data-atc]');
      this.atcLabel = this.querySelector('[data-atc-label]');
      this.error = this.querySelector('[data-size-error]');
      this.walletButtons = this.querySelector('[data-wallet-buttons]');
      this.walletGuard = this.querySelector('[data-wallet-guard]');
      this.sticky = this.querySelector('[data-sticky-bar]');
      this.track = this.querySelector('[data-gallery-track]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.selected = this.initialSelection();
      this.index = 0;

      this.addEventListener('click', (event) => this.onClick(event));
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
      this.qtyInput?.addEventListener('change', () => this.setQuantity(this.qtyInput.value));
      this.track?.addEventListener('scroll', () => this.onTrackScroll(), { passive: true });

      this.onDocumentClick = (event) => {
        const unitButton = event.target.closest('[data-size-unit]');
        if (unitButton) {
          const unit = applySizeUnits(unitButton.dataset.sizeUnit);
          try {
            window.localStorage.setItem(UNITS_KEY, unit);
          } catch (error) {}
          return;
        }
        const fitChip = event.target.closest('[data-fit-size]');
        if (fitChip && !fitChip.disabled && this.sizeIndex > -1) this.select(this.sizeIndex, fitChip.dataset.fitSize);
      };
      this.onOverlayChange = () => this.syncSticky();
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('overlay:change', this.onOverlayChange);

      this.setupSticky();
      applySizeUnits(storedSizeUnits());
      this.update({ initial: true });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('overlay:change', this.onOverlayChange);
      this.stickyObserver?.disconnect();
    }

    initialSelection() {
      const selection = new Array(this.optionCount).fill(null);
      this.querySelectorAll('[data-option-index][aria-pressed="true"]').forEach((button) => {
        selection[Number(button.dataset.optionIndex)] = button.dataset.value;
      });
      const current = this.variants.find((variant) => variant.id === Number(this.idInput?.value));
      for (let i = 0; i < this.optionCount; i++) {
        if (selection[i] != null || i === this.sizeIndex) continue;
        const values = new Set(this.variants.map((variant) => variant.options[i]));
        if (values.size === 1) selection[i] = [...values][0];
        else if (current) selection[i] = current.options[i];
      }
      if (this.sizeIndex > -1 && selection[this.sizeIndex] == null) {
        const sizes = new Set(this.variants.map((variant) => variant.options[this.sizeIndex]));
        if (sizes.size === 1) selection[this.sizeIndex] = [...sizes][0];
      }
      return selection;
    }

    needsSize() {
      return this.sizeIndex > -1 && this.selected[this.sizeIndex] == null;
    }

    currentVariant() {
      if (this.selected.some((value) => value == null)) return null;
      return this.variants.find((variant) => variant.options.every((value, i) => value === this.selected[i])) || null;
    }

    select(index, value) {
      this.selected[index] = value;
      if (index === this.sizeIndex && this.error) this.error.textContent = '';
      this.update({ userChange: true });
    }

    sizeLabel(value) {
      const chip = this.querySelector(`[data-option-index="${this.sizeIndex}"][data-value="${CSS.escape(value)}"]`);
      return chip?.dataset.label || value;
    }

    update({ initial = false, userChange = false } = {}) {
      this.querySelectorAll('[data-option-index]').forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        button.setAttribute('aria-pressed', String(this.selected[index] === button.dataset.value));
      });

      if (this.sizeIndex > -1) {
        this.querySelectorAll(`[data-option-index="${this.sizeIndex}"]`).forEach((chip) => {
          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options[this.sizeIndex] === chip.dataset.value &&
              variant.options.every((value, i) => i === this.sizeIndex || this.selected[i] == null || this.selected[i] === value)
          );
          chip.disabled = !available;
        });
      }

      const variant = this.currentVariant();
      if (variant && this.idInput) {
        this.idInput.value = variant.id;
        if (!initial) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
        const price = this.querySelector('[data-price]');
        if (price) price.textContent = variant.price;
        const compare = this.querySelector('[data-compare]');
        if (compare) {
          compare.textContent = variant.compare || '';
          compare.hidden = !variant.compare;
        }
        const stickyPrice = this.querySelector('[data-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = variant.price;
        if (userChange) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
          if (variant.media) this.goToMedia(variant.media);
        }
      }

      if (this.atc) {
        const soldOut = Boolean(variant && !variant.available);
        this.atc.disabled = soldOut;
        this.atcLabel.textContent = soldOut ? this.atc.dataset.labelSoldOut : this.atc.dataset.labelAdd;
      }

      const needsSize = this.needsSize();
      if (this.walletGuard) this.walletGuard.hidden = !needsSize;
      if (this.walletButtons) this.walletButtons.inert = needsSize || Boolean(variant && !variant.available);

      const sizeValue = this.sizeIndex > -1 ? this.selected[this.sizeIndex] : null;
      const sizeLabel = sizeValue ? this.sizeLabel(sizeValue) : null;
      const stickySize = this.querySelector('[data-sticky-size]');
      if (stickySize && this.sticky) {
        stickySize.textContent = sizeLabel ? fill(this.sticky.dataset.labelSize, { size: sizeLabel }) : this.sticky.dataset.labelPick;
      }

      document.querySelectorAll('[data-fit-size]').forEach((chip) => {
        const matching = this.querySelector(`[data-option-index="${this.sizeIndex}"][data-value="${CSS.escape(chip.dataset.fitSize)}"]`);
        chip.setAttribute('aria-pressed', String(chip.dataset.fitSize === sizeValue));
        if (matching) chip.disabled = matching.disabled;
      });
      document.querySelectorAll('[data-size-row]').forEach((row) => {
        const key = row.dataset.sizeRow.trim().toLowerCase();
        const selected = Boolean(sizeValue) && (key === String(sizeValue).toLowerCase() || key === String(sizeLabel).toLowerCase());
        row.classList.toggle('is-selected', selected);
      });
    }

    onClick(event) {
      const option = event.target.closest('[data-option-index]');
      if (option && !option.disabled) {
        this.select(Number(option.dataset.optionIndex), option.dataset.value);
        return;
      }
      const step = event.target.closest('[data-qty-step]');
      if (step) {
        this.setQuantity(Number(this.qtyInput.value || 1) + Number(step.dataset.qtyStep));
        return;
      }
      const go = event.target.closest('[data-gallery-go]');
      if (go) {
        this.go(Number(go.dataset.galleryGo));
        return;
      }
      const play = event.target.closest('[data-video-play]');
      if (play) {
        const wrapper = play.closest('[data-video]');
        const video = wrapper?.querySelector('video');
        if (video) {
          wrapper.classList.add('is-playing');
          video.controls = true;
          if (window.theme?.playVideo) {
            window.theme.playVideo(video);
          } else {
            const attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(() => {});
          }
          video.focus({ preventScroll: true });
        }
        return;
      }
      if (event.target.closest('[data-wallet-guard]')) {
        this.showSizeError();
        return;
      }
      if (event.target.closest('[data-sticky-atc]')) {
        if (this.needsSize()) {
          this.showSizeError();
          return;
        }
        this.form?.requestSubmit(this.atc || undefined);
      }
    }

    setQuantity(value) {
      const quantity = Math.max(1, Math.min(10, Number.parseInt(value, 10) || 1));
      if (this.qtyInput) this.qtyInput.value = quantity;
    }

    showSizeError() {
      if (!this.error) return;
      this.error.textContent = '';
      requestAnimationFrame(() => {
        this.error.textContent = this.error.dataset.message;
      });
      const group = this.error.closest('.pdp__group');
      const reduced = window.theme?.motion && window.theme.motion() === 'off';
      group?.scrollIntoView({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
      const firstChip = this.querySelector(`[data-option-index="${this.sizeIndex}"]:not(:disabled)`);
      firstChip?.focus({ preventScroll: true });
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.needsSize()) {
        this.showSizeError();
        return;
      }
      const variant = this.currentVariant();
      if (!variant || !variant.available || !window.theme?.cart) return;
      const quantity = Number(this.qtyInput?.value || 1);
      this.atc.setAttribute('aria-busy', 'true');
      this.atc.disabled = true;
      try {
        await window.theme.cart.add([{ id: variant.id, quantity }]);
        this.atcLabel.textContent = this.atc.dataset.labelAdded;
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => {
          this.atcLabel.textContent = this.atc.dataset.labelAdd;
        }, 4000);
        window.theme.cart.open(this.atc);
        const title = this.dataset.productTitle;
        const size = this.sizeIndex > -1 ? this.sizeLabel(this.selected[this.sizeIndex]) : null;
        window.theme.announce(size ? window.theme.t('addedWithSize', { title, size }) : window.theme.t('added', { title }));
      } catch (error) {
        if (this.error) this.error.textContent = error.message;
        window.theme.announce(error.message);
      } finally {
        this.atc.removeAttribute('aria-busy');
        this.atc.disabled = false;
      }
    }

    onTrackScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const width = this.track.clientWidth + 8;
        const count = this.track.children.length;
        const index = Math.max(0, Math.min(count - 1, Math.round(this.track.scrollLeft / width)));
        if (index !== this.index) this.setIndex(index);
      });
    }

    setIndex(index) {
      this.index = index;
      this.querySelectorAll('[data-gallery-go]').forEach((button) => {
        button.setAttribute('aria-current', String(Number(button.dataset.galleryGo) === index));
      });
      Array.from(this.track.children).forEach((slide, i) => {
        if (i === index) return;
        const video = slide.querySelector('video');
        if (video && !video.paused) video.pause();
      });
      const thumbs = this.querySelector('.pdp__thumbs');
      const active = thumbs?.querySelector('[aria-current="true"]');
      if (thumbs && active) {
        const vertical = thumbs.scrollHeight > thumbs.clientHeight;
        if (vertical) thumbs.scrollTo({ top: active.offsetTop - thumbs.clientHeight / 2 + active.clientHeight / 2 });
        else thumbs.scrollTo({ left: active.offsetLeft - thumbs.clientWidth / 2 + active.clientWidth / 2 });
      }
    }

    go(index) {
      if (!this.track) return;
      const reduced = window.theme?.motion && window.theme.motion() === 'off';
      this.track.scrollTo({ left: index * (this.track.clientWidth + 8), behavior: reduced ? 'auto' : 'smooth' });
      this.setIndex(index);
    }

    goToMedia(mediaId) {
      const slides = Array.from(this.track?.children || []);
      const index = slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
      if (index > -1) this.go(index);
    }

    setupSticky() {
      const block = this.querySelector('[data-atc-block]');
      if (!this.sticky || !block || !('IntersectionObserver' in window)) return;
      this.stuck = false;
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        this.stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.syncSticky();
      });
      this.stickyObserver.observe(block);
    }

    syncSticky() {
      if (!this.sticky) return;
      const guideOpen = !document.getElementById('SizeGuide')?.hidden;
      const show = Boolean(this.stuck) && !guideOpen;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.inert = !show;
    }
  }

  customElements.define('product-page', ProductPage);
})();
